import * as React from "react"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js"

import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line } from "react-chartjs-2"
import chartColors from "./chart-colors"

// Switch Language
import { useTranslation } from "react-i18next"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  Tooltip,
  ChartDataLabels
)

/**
 * Internal dependencies.
 */
import "./_chart-lines.scss"
import { values } from "lodash"

const ChartLines = props => {

	const { t } = useTranslation()

  // set atom class
  const atomClassName = "chart-wrap"
  var atomClass =
    props.className != undefined
      ? atomClassName + " " + props.className
      : atomClassName

  const tData = props.tData
  const toggleType = props.toggleType
  const showLines = props.showLines
  const noBackground = props.noBackground
	const skip = props.skip || -1
	const asteriskLabel = props.asteriskLabel || ""

  const htmlLegendPlugin = {
    id: "htmlLegend",
    afterUpdate(chart) {
      const items = chart.canvas.closest('.table-chart-wrap').querySelectorAll("[data-line-src]")

      items.forEach((item, idx) => {
        const btn = item.querySelector("button")

        if (btn && toggleType) {
          btn.onclick = () => {
            if (toggleType == "radio") {
              const clickedRow = btn.closest("tr")
              const clickedRowIdx = Array.from(items).indexOf(clickedRow)

              items.forEach((itemOther, idxOther) => {
                itemOther.classList.remove("active")
                chart.setDatasetVisibility(idxOther, false)
              })

              chart.setDatasetVisibility(clickedRowIdx, true)
            } else {
              let isActive = item.classList.contains("active")
              chart.setDatasetVisibility(idx, !isActive)
            }

            chart.update()
          }
        } else {

					if (showLines){

						if (showLines.indexOf(idx) > -1){
							chart.setDatasetVisibility(idx, true)
						}

					} else {
						chart.setDatasetVisibility(idx, true)
					}
				}
      })
    },
  }

  const chartAreaBorder = {
    id: "chartAreaBorder",
    beforeDraw(chart, args, options) {
      const {
        ctx,
        chartArea: { left, top, width, height },
      } = chart
      ctx.save()
      ctx.strokeStyle = options.borderColor
      ctx.lineWidth = options.borderWidth
      ctx.setLineDash(options.borderDash || [])
      ctx.lineDashOffset = options.borderDashOffset
      ctx.strokeRect(left, top, width, height)
      ctx.restore()
    },
  }

  const custom_canvas_background_color = noBackground ? {} : {
    id: "custom_canvas_background_color",
    beforeDraw: (chart, args, options) => {
      const {
        ctx,
        chartArea: { left, top, width, height },
      } = chart
      ctx.save()

      var grd = ctx.createLinearGradient(0, height, 0, 0)
      grd.addColorStop(0.1, "#eeeeee")
      grd.addColorStop(1, "#ffffff")
      ctx.fillStyle = grd
      ctx.fillRect(left, top, width / 100 * 78.5, height)
      ctx.restore()
    },
  }

  const options = {
    responsive: true,
    layout: {
      padding: {
        left: 0,
      },
    },
    locale: "it-IT",
    scales: {
      x: {
        offset: true,
      },
      y: {
        ticks: {
          stepSize: 10000,
          callback: value => {
            return Math.round(value / 1000)
            // return new Intl.NumberFormat('it-IT',{ maximumSignificantDigits: 3 }).format(value);
          },
        },
      },
    },
    plugins: {
      chartAreaBorder: {
        borderColor: "#dddddd",
        borderWidth: 2,
      },
      htmlLegend: {
        containerID: "tableData",
      },
      legend: {
        display: false,
      },
      tooltip: {
        xAlign: "center",
        yAlign: "top",
        backgroundColor: "#e4eaf0",
        titleColor: "#1d1d1d",
        displayColors: false,
        titleFont: {
          weight: "bold",
        },
        callbacks: {
          labelTextColor: function (context) {
            return "#1d1d1d"
          },
          title: data => {
            var dataset = data[0].parsed.y
						if (skip > -1){
							return data[0].parsed.x === skip+1 ? asteriskLabel : ""
						}
          },
          afterBody: function () {
            return ""
          },
        },
      },
      datalabels: {
        align: '135',
        anchor: 'start',
        offset: 5,
        clamp: true,
        display: 'auto',
        backgroundColor: '#e4eaf0',
        color: '#1d1d1d',
        borderRadius: 4,
        padding: 4,
        textAlign: 'center',
        font: function(context) {
          var w = context.chart.width;
          return {
            size: 12,
            weight: 'normal',
          };
        },
        formatter: function(value, context) {
          if(asteriskLabel && (value === 134427 || value === 24636)){
            // return asteriskLabel
            return t('Cessione del\nbusiness azionamenti')
          } else {
            return null
          }
        }
      }
    },
  }

  const labels = tData.headers[0].cols.slice(1)

  labels.reverse()

  const data = {
    labels,
    datasets: [],
  }

  tData.rows.forEach((row, i) => {
    let dataArr = []

    row.cols.forEach(col => {
      dataArr.push(col.val)
    })

    dataArr.reverse()

    let isHidden =
      (toggleType == "radio" && i == 0) || toggleType == "checkbox"
        ? false
        : true

    const skipVals = (skip, index, preSkipVal, skipVal, defVal) => {
      let ret
      switch (true) {
        case index < skip:
          ret = preSkipVal
          break
        case index == skip:
          ret = skipVal
          break
        default:
          ret = defVal
          break
      }
      return ret
    }

		let pointBgColors = [];

		for (let j = 0; j < dataArr.length; j++) {
			pointBgColors.push(j < skip+1 ? "#7d747d" : chartColors[i])
		}

    data.datasets.push({
      label: row.rowHeader.val,
      data: dataArr,
      hidden: isHidden,
      borderColor: chartColors[i],
      backgroundColor: chartColors[i],
      pointBackgroundColor: pointBgColors,
      pointRadius: 6,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      pointHoverBorderWidth: 2,
      pointBorderColor: "#ffffff",
      segment: {
        borderColor: ctx => {
          return skipVals(
            skip,
            ctx.p0DataIndex,
            "#7d747d",
            "#999999",
            chartColors[i]
          )
        },
        borderDash: ctx => {
          let index = ctx.p0DataIndex
          return index == skip ? [10, 10] : undefined
        },
      },
    })
  })

  return (
    <div className={atomClass}>
      <Line
        options={options}
        data={data}
        plugins={[
          htmlLegendPlugin,
          chartAreaBorder,
          custom_canvas_background_color,
        ]}
      />
    </div>
  )
}

export default ChartLines
